// Duplicate-detection helpers extracted from src/lib/library.ts.
// Pure functions over LibrarySearchResult / LibraryItem — no Supabase calls.
import type {
  LibraryItem,
  LibrarySearchResult,
  LibraryDuplicateClassification,
  LibraryDuplicateMatchReason,
  LibraryDuplicateWarning,
} from "./library";

const normalizeIdentifier = (value: string | null | undefined) =>
  value?.replace(/[^0-9xX]/g, "").toUpperCase() ?? "";

const normalizeText = (value: string | null | undefined) =>
  value?.trim().toLocaleLowerCase().replace(/\s+/g, " ") ?? "";

const getDuplicateClassification = (
  draft: Pick<LibrarySearchResult, "edition" | "volume" | "publishedYear" | "languageCode">,
  existing: Pick<LibraryItem, "edition" | "volume" | "published_year" | "language_code">
): LibraryDuplicateClassification => {
  const editionChanged = normalizeText(draft.edition) && normalizeText(existing.edition) && normalizeText(draft.edition) !== normalizeText(existing.edition);
  const volumeChanged = normalizeText(draft.volume) && normalizeText(existing.volume) && normalizeText(draft.volume) !== normalizeText(existing.volume);
  const languageChanged = normalizeText(draft.languageCode) && normalizeText(existing.language_code) && normalizeText(draft.languageCode) !== normalizeText(existing.language_code);
  const yearChanged = draft.publishedYear && existing.published_year && draft.publishedYear !== existing.published_year;

  if (languageChanged || volumeChanged) return "translation-or-volume";
  if (editionChanged || yearChanged) return "alternate-edition";
  return "same-record";
};

const describeComparison = (
  draft: Pick<LibrarySearchResult, "edition" | "volume" | "publishedYear" | "languageCode">,
  existing: Pick<LibraryItem, "edition" | "volume" | "published_year" | "language_code">
) => {
  const comparison: string[] = [];
  if (draft.publishedYear && existing.published_year && draft.publishedYear !== existing.published_year) {
    comparison.push(`Year ${existing.published_year} vs ${draft.publishedYear}`);
  }
  if (normalizeText(draft.edition) && normalizeText(existing.edition) && normalizeText(draft.edition) !== normalizeText(existing.edition)) {
    comparison.push(`Edition ${existing.edition} vs ${draft.edition}`);
  }
  if (normalizeText(draft.volume) && normalizeText(existing.volume) && normalizeText(draft.volume) !== normalizeText(existing.volume)) {
    comparison.push(`Volume ${existing.volume} vs ${draft.volume}`);
  }
  if (normalizeText(draft.languageCode) && normalizeText(existing.language_code) && normalizeText(draft.languageCode) !== normalizeText(existing.language_code)) {
    comparison.push(`Language ${existing.language_code} vs ${draft.languageCode}`);
  }
  return comparison;
};

const isDistinctEdition = (
  draft: Pick<LibrarySearchResult, "edition" | "volume" | "publishedYear">,
  existing: Pick<LibraryItem, "edition" | "volume" | "published_year">,
) => {
  const editionChanged = normalizeText(draft.edition) && normalizeText(existing.edition) && normalizeText(draft.edition) !== normalizeText(existing.edition);
  const volumeChanged = normalizeText(draft.volume) && normalizeText(existing.volume) && normalizeText(draft.volume) !== normalizeText(existing.volume);
  const publishedYearChanged = draft.publishedYear && existing.published_year && draft.publishedYear !== existing.published_year;
  return Boolean(editionChanged || volumeChanged || publishedYearChanged);
};

export const findLikelyDuplicateLibraryItems = (
  draft: LibrarySearchResult,
  items: LibraryItem[],
  currentId?: string,
): LibraryDuplicateWarning[] => {
  const normalizedIsbn13 = normalizeIdentifier(draft.isbn13);
  const normalizedIsbn10 = normalizeIdentifier(draft.isbn10);
  const normalizedTitle = normalizeText(draft.displayTitle || draft.title);
  const normalizedAuthor = normalizeText(draft.authorText || draft.authors.join(", "));
  const normalizedSourceId = normalizeText(draft.sourceId);
  const normalizedGoogleBooksId = normalizeText(draft.googleBooksId);
  const normalizedOpenLibraryId = normalizeText(draft.openLibraryId);
  const normalizedDoi = normalizeText(draft.doi)?.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "") ?? null;
  const normalizedUrl = normalizeText(draft.url)?.replace(/^https?:\/\//i, "").replace(/\/+$/, "") ?? null;

  return items
    .filter((item) => item.id !== currentId)
    .map((item) => {
      const distinctEdition = isDistinctEdition(draft, item);
      const classification = getDuplicateClassification(draft, item);
      const comparison = describeComparison(draft, item);
      const itemTitle = normalizeText(item.display_title || item.title);
      const itemAuthor = normalizeText(item.author_text);

      if (normalizedIsbn13 && normalizedIsbn13 === normalizeIdentifier(item.isbn_13)) {
        return {
          item,
          reason: "isbn13" as const,
          severity: distinctEdition ? "possible" as const : "strong" as const,
          classification,
          headline: distinctEdition ? "Same ISBN-13, different edition signals" : "Exact ISBN-13 match",
          detail: distinctEdition
            ? "This shares an ISBN-13 with an existing record, but the year, edition, or volume suggests you may be tracking a distinct version."
            : "This shares an ISBN-13 with an existing record and is very likely the same source.",
          comparison,
        };
      }

      if (normalizedIsbn10 && normalizedIsbn10 === normalizeIdentifier(item.isbn_10)) {
        return {
          item,
          reason: "isbn10" as const,
          severity: distinctEdition ? "possible" as const : "strong" as const,
          classification,
          headline: distinctEdition ? "Same ISBN-10, different edition signals" : "Exact ISBN-10 match",
          detail: distinctEdition
            ? "This shares an ISBN-10 with an existing record, but the year, edition, or volume suggests it may be a separate version."
            : "This shares an ISBN-10 with an existing record and is very likely the same source.",
          comparison,
        };
      }

      const itemDoi = normalizeText(item.doi)?.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "") ?? null;
      if (normalizedDoi && itemDoi && normalizedDoi === itemDoi) {
        return {
          item,
          reason: "doi" as const,
          severity: "strong" as const,
          classification,
          headline: "DOI already saved",
          detail: "An existing source in My Library has the same DOI.",
          comparison,
        };
      }

      const itemUrl = normalizeText(item.url)?.replace(/^https?:\/\//i, "").replace(/\/+$/, "") ?? null;
      if (normalizedUrl && itemUrl && normalizedUrl === itemUrl) {
        return {
          item,
          reason: "url" as const,
          severity: "strong" as const,
          classification,
          headline: "URL already saved",
          detail: "An existing source in My Library has the same URL.",
          comparison,
        };
      }

      const matchesProvider = (
        (normalizedSourceId && normalizedSourceId === normalizeText(item.source_id))
        || (normalizedGoogleBooksId && normalizedGoogleBooksId === normalizeText(item.google_books_id))
        || (normalizedOpenLibraryId && normalizedOpenLibraryId === normalizeText(item.open_library_id))
      );

      if (draft.sourceProvider !== "manual" && matchesProvider) {
        return {
          item,
          reason: "provider" as const,
          severity: distinctEdition ? "possible" as const : "strong" as const,
          classification,
          headline: distinctEdition ? "Same provider source, different edition signals" : "Provider record already saved",
          detail: distinctEdition
            ? "This points to the same imported provider record, but the edition clues differ enough that you may want both entries."
            : "This points to the same imported provider record that already exists in your library.",
          comparison,
        };
      }

      if (normalizedTitle && normalizedAuthor && normalizedTitle === itemTitle && normalizedAuthor === itemAuthor) {
        return {
          item,
          reason: "title-author" as const,
          severity: distinctEdition ? "possible" as const : "strong" as const,
          classification,
          headline: distinctEdition ? "Title and author match, but edition clues differ" : "Title and author match an existing record",
          detail: distinctEdition
            ? "The core title and author match, but the year, edition, or volume may indicate a translation, revision, or separate printing."
            : "The core title and author already match an existing record closely enough to review before saving.",
          comparison,
        };
      }

      return null;
    })
    .filter((warning): warning is NonNullable<typeof warning> => Boolean(warning))
    .sort((a, b) => {
      const severityWeight = { strong: 0, possible: 1 } as const;
      const reasonWeight: Record<LibraryDuplicateMatchReason, number> = { isbn13: 0, isbn10: 1, doi: 2, url: 3, provider: 4, "title-author": 5 };
      return severityWeight[a.severity] - severityWeight[b.severity] || reasonWeight[a.reason] - reasonWeight[b.reason];
    });
};
